import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Award, CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Globe2,
  GraduationCap, Handshake, Home, Images, Medal, Users, X,
} from 'lucide-react';
import SEO from '../components/SEO';
import { fetchPrestasiMahasiswaBySlug, getImageUrl } from '../utils/api';

const CATEGORY_LABELS = {
  penalaran: 'Penalaran & Inovasi',
  seni: 'Seni & Budaya',
  kompetisi: 'Kompetisi',
  kepemimpinan: 'Kepemimpinan',
  lainnya: 'Lainnya',
};

const categoryLabel = (category) => CATEGORY_LABELS[category] || category || 'Prestasi Mahasiswa';

// Jarak seret minimum (px) sebelum dianggap swipe, bukan sekadar tap/scroll
// tak sengaja.
const SWIPE_THRESHOLD_PX = 50;

/// Lightbox galeri dengan navigasi panah, keyboard (←/→/Esc), dan swipe geser
/// layar (mobile & desktop) — muter ke ujung lain saat sampai foto pertama/
/// terakhir.
function GalleryLightbox({ photos, index, onIndexChange, onClose }) {
  const touchStart = useRef(null);
  const total = photos.length;

  const go = (delta) => onIndexChange((index + delta + total) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && total > 1) go(-1);
      else if (e.key === 'ArrowRight' && total > 1) go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    // Abaikan seretan yang lebih vertikal daripada horizontal (scroll biasa).
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;
    if (total > 1) go(dx < 0 ? 1 : -1);
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/85 p-4 touch-pan-y"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Tutup">
        <X size={20} />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 sm:left-4"
            aria-label="Foto sebelumnya"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 sm:right-4"
            aria-label="Foto berikutnya"
          >
            <ChevronRight size={24} />
          </button>
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white">
            {index + 1} / {total}
          </span>
        </>
      )}

      <img
        key={photos[index].id}
        src={getImageUrl(photos[index].url, { width: 1400 })}
        alt="Pratinjau foto"
        className="max-h-[85vh] max-w-full select-none rounded-2xl object-contain"
        draggable={false}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

const PrestasiMahasiswaDetail = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPrestasiMahasiswaBySlug(slug)
      .then((data) => { if (active) setItem(data); })
      .catch(() => { if (active) setItem(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="grid min-h-[60vh] place-items-center text-sm text-gray-400">Memuat prestasi…</div>;
  }

  if (!item) {
    return (
      <div className="grid min-h-[60vh] place-items-center px-4">
        <div className="text-center">
          <Award className="mx-auto text-purple-300" size={42} />
          <h1 className="mt-4 font-heading text-xl font-bold text-[#1A1A2E] dark:text-white">Prestasi tidak ditemukan</h1>
          <Link to="/prestasi-mahasiswa" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300">Kembali ke daftar prestasi <ArrowRight size={15} /></Link>
        </div>
      </div>
    );
  }

  const gallery = item.gallery || [];
  const description = String(item.description || '').replace(/\s+/g, ' ').trim();
  const seoKeywords = [
    item.title,
    'prestasi mahasiswa STIKES Harapan Ibu Jambi',
    categoryLabel(item.category),
    item.level,
    item.studentName,
    item.programStudy,
  ].filter(Boolean);

  return (
    <div data-testid="prestasi-mahasiswa-detail-page">
      <SEO
        title={`${item.title} | Prestasi Mahasiswa`}
        description={description}
        image={item.imageUrl}
        type="article"
        article={{
          publishedTime: item.createdAt,
          modifiedTime: item.updatedAt || item.createdAt,
          section: categoryLabel(item.category),
          author: 'STIKES Harapan Ibu Jambi',
        }}
        breadcrumb={[
          { name: 'Kemahasiswaan & Alumni', path: '/unit/kemahasiswaan' },
          { name: 'Prestasi Mahasiswa', path: '/prestasi-mahasiswa' },
          { name: item.title },
        ]}
        keywords={seoKeywords}
      />

      <div className="page-hero pt-[70px]">
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
              <Link to="/" className="flex items-center gap-1 transition-colors hover:text-white"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <Link to="/unit/kemahasiswaan" className="transition-colors hover:text-white">Kemahasiswaan &amp; Alumni</Link>
              <ChevronRight size={14} />
              <Link to="/prestasi-mahasiswa" className="transition-colors hover:text-white">Prestasi Mahasiswa</Link>
              <ChevronRight size={14} />
              <span className="text-white truncate">{item.title}</span>
            </div>
            <h1 className="max-w-3xl font-heading text-3xl font-bold text-white md:text-4xl">{item.title}</h1>
          </motion.div>
        </div>
      </div>

      <main className="bg-[#F8F7FF] dark:bg-[#121020]">
        <section className="py-10 sm:py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[360px_minmax(0,1fr)]">
              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:sticky lg:top-24 h-fit">
                <div className="overflow-hidden rounded-3xl border border-purple-100 bg-white shadow-sm dark:border-purple-900/30 dark:bg-[#1A182E]">
                  <div className="aspect-[3/4] overflow-hidden bg-purple-100 dark:bg-purple-950/30">
                    {item.imageUrl
                      ? <img src={getImageUrl(item.imageUrl, { width: 720 })} alt={item.title} className="h-full w-full object-cover" style={{ objectPosition: '50% 60%' }} />
                      : <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#7B2FBE] to-[#4C1D95] text-5xl font-bold text-white"><Award size={48} /></div>}
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wide">
                      <span className="rounded-full bg-purple-100 px-3 py-1 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">{CATEGORY_LABELS[item.category] || item.category}</span>
                      {item.year && <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-500 dark:bg-white/10 dark:text-gray-400">{item.year}</span>}
                    </div>
                    <dl className="mt-5 space-y-3 text-sm">
                      <div className="flex items-center gap-3"><GraduationCap size={16} className="shrink-0 text-[#7B2FBE]" /><div><dt className="text-xs text-gray-400">Mahasiswa / Tim</dt><dd className="font-semibold text-[#1A1A2E] dark:text-white">{item.studentName}</dd></div></div>
                      <div className="flex items-center gap-3"><Medal size={16} className="shrink-0 text-[#7B2FBE]" /><div><dt className="text-xs text-gray-400">Tingkat</dt><dd className="font-semibold text-[#1A1A2E] dark:text-white">{item.level}</dd></div></div>
                      {item.programStudy && <div className="flex items-center gap-3"><Users size={16} className="shrink-0 text-[#7B2FBE]" /><div><dt className="text-xs text-gray-400">Program Studi</dt><dd className="font-semibold text-[#1A1A2E] dark:text-white">{item.programStudy}</dd></div></div>}
                      {item.organizer && <div className="flex items-center gap-3"><Handshake size={16} className="shrink-0 text-[#7B2FBE]" /><div><dt className="text-xs text-gray-400">Penyelenggara</dt><dd className="font-semibold text-[#1A1A2E] dark:text-white">{item.organizer}</dd></div></div>}
                    </dl>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300"><Award size={15} /> Tentang prestasi</div>
                <p className="mt-4 whitespace-pre-line break-words text-base leading-relaxed text-gray-700 dark:text-gray-300">{item.description}</p>
                {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300">Lihat dokumentasi resmi <ExternalLink size={14} /></a>}

                <div className="mt-10">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300"><Images size={15} /> Galeri dokumentasi</div>
                  <h2 className="mt-3 font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white">Momen dan capaian</h2>
                  {gallery.length === 0 ? (
                    <div className="mt-5 rounded-2xl border border-dashed border-gray-200 py-12 text-center dark:border-gray-700">
                      <Images className="mx-auto text-gray-300 dark:text-gray-600" size={36} />
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Belum ada foto dokumentasi untuk prestasi ini.</p>
                    </div>
                  ) : (
                    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {gallery.map((photo, index) => (
                        <button key={photo.id} type="button" onClick={() => setLightbox(index)} className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-white/5">
                          <img src={getImageUrl(photo.url, { width: 600 })} alt={`Dokumentasi ${index + 1}`} className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/prestasi-mahasiswa" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300"><ArrowLeft size={15} /> Kembali ke daftar prestasi</Link>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      {lightbox !== null && (
        <GalleryLightbox
          photos={gallery}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default PrestasiMahasiswaDetail;
